// Групповые звонки (mesh, см. отчёт пользователя "групповые звонки нужно попробовать
// реализовать"): один RtcPeerConnection на каждого собеседника, сигналинг — тот же WS-канал,
// что и у 1:1 звонков (/app/rtc/{chatId} → /topic/chats/{chatId}/rtc, см. rtc.ts), просто с
// новыми типами сообщений (join/leave/offer/answer/ice/roster) и адресацией toUserId — сервер
// сам ничего не решает и по-прежнему только ретранслирует (см. RtcWs), кроме ростера
// "кто уже в звонке" (это единственное, что нужно спросить у сервера явно).
import { realtime } from './realtime';
import { applyIceServers, currentIceServers } from './rtcIce';

export interface GroupRtcSignal {
  type: 'join' | 'leave' | 'offer' | 'answer' | 'ice' | 'roster' | 'mute-state';
  fromUserId?: string;
  toUserId?: string;
  sdp?: RTCSessionDescriptionInit;
  candidate?: RTCIceCandidateInit | null;
  members?: string[]; // только у 'roster'
  // Только у 'mute-state' (см. отчёт пользователя "добавить кнопку выключить звук и
  // отображать всем что звук выключен/включен") — без toUserId, рассылается всем сразу,
  // как join/leave: статус мьюта должны видеть все участники, а не один конкретный.
  kind?: 'audio' | 'video';
  muted?: boolean;
}

// 'disconnected' часто транзитно — даём этот запас времени на restartIce() прежде чем считать
// пира отвалившимся (см. rtc.ts, тот же приём для 1:1).
const ICE_RESTART_DELAY_MS = 6000;
export const rtcGroupApp = (chatId: string) => `/app/rtc/${chatId}`;

export interface GroupRtcHandlers {
  onRemoteStream: (userId: string, stream: MediaStream) => void;
  onPeerLeft: (userId: string) => void;
  /** Ростер участников на момент входа — до того, как от них реально придёт медиа (для
   *  мгновенного показа плашек "подключение…", а не пустого экрана). */
  onRoster: (memberIds: string[]) => void;
  /** Участник включил/выключил микрофон или камеру. */
  onPeerMuteState?: (userId: string, kind: 'audio' | 'video', muted: boolean) => void;
}

export class GroupCallSession {
  readonly chatId: string;
  readonly meId: string;
  localStream?: MediaStream;
  private handlers: GroupRtcHandlers;
  private peers = new Map<string, RTCPeerConnection>();
  private pendingCandidates = new Map<string, RTCIceCandidateInit[]>();
  private remoteDescSet = new Set<string>();
  private disconnectTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private joined = false;
  private destroyed = false;

  constructor(chatId: string, meId: string, handlers: GroupRtcHandlers) {
    this.chatId = chatId;
    this.meId = meId;
    this.handlers = handlers;
  }

  async startWithMedia(audio: boolean, video: boolean): Promise<void> {
    this.localStream = await navigator.mediaDevices.getUserMedia({ audio, video });
  }

  /** Войти в звонок этого чата — рассылает "join" (существующие сами предложат оффер, либо
   *  я сам предложу тем, у кого id "меньше" меня — см. shouldOffer). */
  join() {
    this.joined = true;
    this.signal({ type: 'join' });
  }

  leave() {
    this.signal({ type: 'leave' });
    this.destroy();
  }

  /** Включить/выключить микрофон для всех собеседников разом — трек остаётся в каждом
   *  RTCPeerConnection (без renegotiation), просто перестаёт передавать звук; статус шлём
   *  явным сигналом, а не полагаемся на события mute/unmute у собеседников (ненадёжны в
   *  разных браузерах). Возвращает новое состояние (true = выключен). */
  toggleMic(): boolean {
    const track = this.localStream?.getAudioTracks()[0];
    if (!track) return false;
    track.enabled = !track.enabled;
    this.signal({ type: 'mute-state', kind: 'audio', muted: !track.enabled });
    return !track.enabled;
  }

  /** Включить/выключить камеру — тот же приём, что и toggleMic, для видео-дорожки. */
  toggleCamera(): boolean {
    const track = this.localStream?.getVideoTracks()[0];
    if (!track) return false;
    track.enabled = !track.enabled;
    this.signal({ type: 'mute-state', kind: 'video', muted: !track.enabled });
    return !track.enabled;
  }

  async onSignal(sig: GroupRtcSignal) {
    if (this.destroyed || sig.fromUserId === this.meId) return;
    if (sig.toUserId && sig.toUserId !== this.meId) return; // адресовано не мне
    if (sig.type === 'roster') {
      const members = (sig.members ?? []).filter((id) => id !== this.meId);
      this.handlers.onRoster(members);
      for (const id of members) {
        if (this.shouldOffer(id)) await this.offerTo(id);
      }
      return;
    }
    const from = sig.fromUserId;
    if (!from) return;
    try {
      if (sig.type === 'join') {
        // Я уже в звонке, кто-то новый зашёл — предлагаю оффер, только если выигрываю
        // тай-брейк (иначе он предложит мне сам, см. roster-ветку у него).
        if (this.joined && this.shouldOffer(from) && !this.peers.has(from)) await this.offerTo(from);
      } else if (sig.type === 'offer' && sig.sdp) {
        const pc = this.getOrCreatePeer(from);
        await pc.setRemoteDescription(sig.sdp);
        this.remoteDescSet.add(from);
        await this.flushPending(from, pc);
        await applyIceServers(pc);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        this.signal({ type: 'answer', toUserId: from, sdp: answer });
      } else if (sig.type === 'answer' && sig.sdp) {
        const pc = this.peers.get(from);
        if (!pc) return;
        await pc.setRemoteDescription(sig.sdp);
        this.remoteDescSet.add(from);
        await this.flushPending(from, pc);
      } else if (sig.type === 'ice' && sig.candidate) {
        const pc = this.peers.get(from);
        if (!pc) return;
        if (this.remoteDescSet.has(from)) {
          await pc.addIceCandidate(sig.candidate);
        } else {
          const arr = this.pendingCandidates.get(from) ?? [];
          arr.push(sig.candidate);
          this.pendingCandidates.set(from, arr);
        }
      } else if (sig.type === 'leave') {
        this.removePeer(from);
      } else if (sig.type === 'mute-state' && sig.kind && sig.muted != null) {
        this.handlers.onPeerMuteState?.(from, sig.kind, sig.muted);
      }
    } catch (e) {
      // Транзитная ошибка (битый candidate/гонка состояний) не должна рвать весь звонок —
      // остальные пары как были, так и остаются на связи.
      console.warn('[rtc-group] signal error', e);
    }
  }

  /** Детерминированный тай-брейк "кто первый предлагает оффер" — без него оба участника
   *  пары, узнав друг о друге почти одновременно (roster у одного + join-broadcast у
   *  другого), отправили бы offer друг другу одновременно ("glare"), и один из двух
   *  RTCPeerConnection свалился бы в невалидное состояние. Сравнение id стабильно с обеих
   *  сторон — офферит ровно один из двух, независимо от того, кто раньше присоединился. */
  private shouldOffer(peerId: string): boolean {
    return this.meId > peerId;
  }

  private async offerTo(peerId: string) {
    const pc = this.getOrCreatePeer(peerId);
    await applyIceServers(pc);
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    this.signal({ type: 'offer', toUserId: peerId, sdp: offer });
  }

  private async flushPending(peerId: string, pc: RTCPeerConnection) {
    const pending = this.pendingCandidates.get(peerId);
    if (!pending) return;
    for (const c of pending) {
      try { await pc.addIceCandidate(c); } catch { /* битый кандидат */ }
    }
    this.pendingCandidates.delete(peerId);
  }

  private getOrCreatePeer(peerId: string): RTCPeerConnection {
    const existing = this.peers.get(peerId);
    if (existing) return existing;
    const pc = new RTCPeerConnection({ iceServers: currentIceServers() });
    pc.onicecandidate = (e) => this.signal({ type: 'ice', toUserId: peerId, candidate: e.candidate });
    pc.ontrack = (e) => this.handlers.onRemoteStream(peerId, e.streams[0]);
    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      if (state === 'connected') {
        this.clearDisconnectTimer(peerId);
      } else if (state === 'disconnected') {
        // См. rtc.ts — тот же приём: не убираем пира сразу, даём шанс на restartIce().
        this.clearDisconnectTimer(peerId);
        const timer = setTimeout(() => {
          if (this.destroyed || pc.connectionState !== 'disconnected') return;
          try { pc.restartIce(); } catch { /* не поддерживается или уже закрыт */ }
        }, ICE_RESTART_DELAY_MS);
        this.disconnectTimers.set(peerId, timer);
      } else if (state === 'failed' || state === 'closed') {
        this.clearDisconnectTimer(peerId);
        this.removePeer(peerId);
      }
    };
    this.localStream?.getTracks().forEach((t) => pc.addTrack(t, this.localStream!));
    this.peers.set(peerId, pc);
    return pc;
  }

  private clearDisconnectTimer(peerId: string) {
    const timer = this.disconnectTimers.get(peerId);
    if (timer != null) {
      clearTimeout(timer);
      this.disconnectTimers.delete(peerId);
    }
  }

  private removePeer(peerId: string) {
    const pc = this.peers.get(peerId);
    if (!pc) return;
    this.clearDisconnectTimer(peerId);
    pc.close();
    this.peers.delete(peerId);
    this.pendingCandidates.delete(peerId);
    this.remoteDescSet.delete(peerId);
    this.handlers.onPeerLeft(peerId);
  }

  private signal(body: GroupRtcSignal) {
    realtime.publish(rtcGroupApp(this.chatId), body);
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const timer of this.disconnectTimers.values()) clearTimeout(timer);
    this.disconnectTimers.clear();
    this.localStream?.getTracks().forEach((t) => t.stop());
    for (const [, pc] of this.peers) pc.close();
    this.peers.clear();
    this.pendingCandidates.clear();
    this.remoteDescSet.clear();
  }
}
